import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import { AppShell, useShell } from '../components/AppShell';
import { Search, Plus } from 'lucide-react';
import PhoneInput from '../components/PhoneInput';
import DuplicateCustomerModal from '../components/DuplicateCustomerModal';
import { EmptyState } from '../components/EmptyState';
import { isValidUKMobile, formatPhoneForDisplay } from '../lib/phone';

const initials = (name) => name?.split(/\s+/).map(w => w[0]).slice(0, 2).join('').toUpperCase() || '?';

const timeAgo = (iso) => {
  if (!iso) return '—';
  const d = new Date(iso);
  const ms = Date.now() - d.getTime();
  const day = 24 * 60 * 60 * 1000;
  const days = Math.floor(ms / day);
  if (days <= 0) return 'Today';
  if (days === 1) return '1 day ago';
  if (days < 7) return `${days} days ago`;
  const wks = Math.floor(days / 7);
  if (wks === 1) return '1 wk ago';
  if (wks < 8) return `${wks} wks ago`;
  const mo = Math.floor(days / 30);
  if (mo === 1) return '1 mo ago';
  return `${mo} mo ago`;
};

const segmentFor = (visits, lastVisit) => {
  if (visits === 0) return null;
  if (visits === 1) return { cls: 'green', label: 'New' };
  const daysSince = lastVisit ? (Date.now() - new Date(lastVisit).getTime()) / 86400000 : Infinity;
  if (visits >= 5 && daysSince <= 60) return { cls: 'prem', label: 'Regular' };
  if (daysSince > 30) return { cls: 'amber', label: 'Lapsing' };
  return null;
};

const ClientsInner = () => {
  const { profile } = useShell();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState(params.get('q') || '');
  // Alphabet filter — '' means "All". Clicking a letter narrows the
  // table to clients whose display name starts with that letter after
  // stripping any leading punctuation ('#Bob' → 'Bob' → 'B').
  const [letter, setLetter] = useState('');
  const [showAdd, setShowAdd] = useState(false);
  // Visible page window. Caps the rendered rows at PAGE_SIZE so a shop
  // with 5k clients doesn't render 5k <tr>s (was pinned as an "endless
  // scroll" complaint). "Show more" reveals another page; letter + q
  // reset the window back to one page.
  const PAGE_SIZE = 50;
  const [visibleCount, setVisibleCount] = useState(PAGE_SIZE);

  const shopId = profile?.shop_id;

  useEffect(() => {
    if (!shopId) return;
    loadClients(shopId);
  }, [shopId]);

  const loadClients = async (sid) => {
    setLoading(true);
    // Pull all customers for the shop
    const { data: customers } = await supabase
      .from('customers')
      .select('id,name,phone,email,created_at')
      .eq('shop_id', sid)
      .order('name');

    // Pull bookings to compute aggregates
    const { data: bookings } = await supabase
      .from('bookings')
      .select('customer_id,starts_at,status,deposit_amount,service:services(name,price)')
      .eq('shop_id', sid);

    const agg = {};
    const now = Date.now();
    (bookings || []).forEach(b => {
      const a = agg[b.customer_id] || (agg[b.customer_id] = {
        visits: 0, spend: 0, lastVisit: null, lastService: null,
      });
      const t = new Date(b.starts_at).getTime();
      // Only count PAST (already happened) bookings for "last visit"
      if (t <= now && (b.status === 'completed' || b.status === 'confirmed' || b.status === 'no_show')) {
        if (!a.lastVisit || t > new Date(a.lastVisit).getTime()) {
          a.lastVisit = b.starts_at;
          a.lastService = b.service?.name || '—';
        }
      }
      if (b.status === 'completed') {
        a.visits += 1;
        a.spend  += Number(b.service?.price || 0);
      }
    });

    const merged = (customers || []).map(c => ({
      ...c,
      visits: agg[c.id]?.visits || 0,
      spend:  agg[c.id]?.spend  || 0,
      lastVisit:   agg[c.id]?.lastVisit   || null,
      lastService: agg[c.id]?.lastService || '—',
    }));
    setRows(merged);
    setLoading(false);
  };

  // First alphabet letter of the name after stripping leading
  // punctuation/whitespace. Non-alpha names (e.g. "007", "—") return
  // null and are only visible under "All".
  const firstAlpha = (name) => {
    const m = (name || '').replace(/^[^A-Za-z]+/, '')[0];
    return m ? m.toUpperCase() : null;
  };

  // Which letters actually have at least one client (ignoring the
  // search query — the strip reflects the underlying dataset, not the
  // active text search). Letters not in this set render disabled.
  const presentLetters = useMemo(() => {
    const s = new Set();
    rows.forEach(r => { const c = firstAlpha(r.name); if (c) s.add(c); });
    return s;
  }, [rows]);

  const filtered = useMemo(() => {
    const s = q.trim().toLowerCase();
    let out = rows;
    if (s) {
      out = out.filter(r =>
        r.name?.toLowerCase().includes(s) ||
        r.phone?.toLowerCase().includes(s) ||
        r.email?.toLowerCase().includes(s)
      );
    }
    if (letter) {
      out = out.filter(r => firstAlpha(r.name) === letter);
    }
    return out;
  }, [q, rows, letter]);

  // Reset visible-window whenever the filter set changes so a search
  // for a client at the bottom of the list always shows them at the
  // top of the newly-filtered set.
  useEffect(() => { setVisibleCount(PAGE_SIZE); }, [q, letter]);
  const visibleRows = filtered.slice(0, visibleCount);
  const hasMore = filtered.length > visibleCount;

  return (
    <main className="main">
      <div className="topbar">
        <div>
          <h2>Clients</h2>
          <div className="sub">
            {rows.length} client{rows.length === 1 ? '' : 's'} · you own this list
          </div>
        </div>
        <div style={{ marginLeft: 'auto', display: 'flex', gap: 8 }}>
          <div className="search">
            <Search size={14} />
            <input
              placeholder="Search name or phone…"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              data-testid="clients-search"
            />
          </div>
          <button className="btn p" onClick={() => setShowAdd(true)} data-testid="clients-add-btn">
            <Plus size={14} /> Add client
          </button>
        </div>
      </div>

      <div className="body">
        {/* A–Z filter strip. Sits inside .body so its top offset from
            the header is .body's existing 22px padding-top — no new
            spacing invented. Its own bottom margin matches that same
            22px so the header→strip and strip→card gaps are identical. */}
        <div
          data-testid="clients-alpha-filter"
          style={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: 6,
            marginBottom: 22,
          }}
        >
          {(() => {
            const letters = ['All', ...'ABCDEFGHIJKLMNOPQRSTUVWXYZ'.split('')];
            return letters.map(L => {
              const isAll = L === 'All';
              const active = isAll ? letter === '' : letter === L;
              const disabled = !isAll && !presentLetters.has(L);
              return (
                <button
                  key={L}
                  type="button"
                  data-testid={isAll ? 'alpha-btn-all' : `alpha-btn-${L}`}
                  disabled={disabled}
                  aria-pressed={active}
                  onClick={() => setLetter(isAll ? '' : L)}
                  style={{
                    // Small pill — no new tokens; reuses the same
                    // border/paper/ink vars already used elsewhere on
                    // this page (search input, panels).
                    minWidth: 30,
                    height: 30,
                    padding: '0 9px',
                    border: '1px solid var(--line)',
                    borderRadius: 8,
                    background: active ? 'var(--ink)' : 'var(--card)',
                    color: active ? '#fff' : (disabled ? 'var(--mute2)' : 'var(--ink)'),
                    fontFamily: 'inherit',
                    fontSize: 12.5,
                    fontWeight: 600,
                    cursor: disabled ? 'default' : 'pointer',
                    opacity: disabled ? 0.4 : 1,
                    lineHeight: 1,
                  }}
                >
                  {L}
                </button>
              );
            });
          })()}
        </div>
        <div className="panel" data-testid="clients-table">
          {loading ? (
            <div style={{ padding: 20 }}>
              {[1,2,3,4].map(i => <div key={i} className="skel-line" style={{ width: `${60 + i*8}%` }} />)}
            </div>
          ) : filtered.length === 0 ? (
            q.trim() ? (
              <EmptyState
                title={`No clients match "${q.trim()}"`}
                detail="Try a different name or phone number."
                dataTestId="clients-empty"
              />
            ) : (
              <EmptyState
                title="No clients yet"
                detail="When clients book, they'll show up here."
                dataTestId="clients-empty"
              />
            )
          ) : (
            <table className="table">
              <thead>
                <tr>
                  <th>Clients</th>
                  <th>Last visit</th>
                  <th>Last service</th>
                  <th>Visits</th>
                  <th>Spend</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {visibleRows.map(c => {
                  const seg = segmentFor(c.visits, c.lastVisit);
                  return (
                    <tr
                      key={c.id}
                      className="clickable"
                      onClick={() => navigate(`/clients/${c.id}`)}
                      data-testid={`client-row-${c.id}`}
                    >
                      <td data-th="Client">
                        <div className="who">
                          <span className="circ">{initials(c.name)}</span>
                          <div>
                            <b>{c.name}</b>
                            <small>{formatPhoneForDisplay(c.phone) || c.email || '—'}</small>
                          </div>
                        </div>
                      </td>
                      <td className="mono" data-th="Last visit">{timeAgo(c.lastVisit)}</td>
                      <td data-th="Last service">{c.lastService}</td>
                      <td className="mono" data-th="Visits">{c.visits}</td>
                      <td className="mono" data-th="Spend">£{c.spend.toFixed(0)}</td>
                      <td className="tac" data-th="Segment">
                        {seg && <span className={`pill ${seg.cls}`}>{seg.label}</span>}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
          {hasMore && (
            <div style={{ padding: 14, textAlign: 'center', borderTop: '1px solid var(--line)' }}>
              <span style={{ fontSize: 12, color: 'var(--mute)', marginRight: 12 }}>
                Showing {visibleRows.length} of {filtered.length}
              </span>
              <button
                type="button"
                className="btn s"
                onClick={() => setVisibleCount(c => c + PAGE_SIZE)}
                data-testid="clients-show-more"
              >
                Show more
              </button>
            </div>
          )}
        </div>
      </div>

      {showAdd && (
        <AddClientModal
          shopId={shopId}
          onClose={() => setShowAdd(false)}
          onCreated={() => { setShowAdd(false); loadClients(shopId); }}
        />
      )}
    </main>
  );
};

const AddClientModal = ({ shopId, onClose, onCreated }) => {
  const [form, setForm] = useState({ name: '', phone: '', email: '' });
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);
  const [duplicateCandidate, setDuplicateCandidate] = useState(null);

  const submit = async (e) => {
    e.preventDefault();
    setError('');

    // UK-format guard. Non-UK entry is no longer supported from this
    // modal — the toggle was removed. A blank phone stays optional
    // (PhoneInput has required={false}).
    if (form.phone && !isValidUKMobile(form.phone)) {
      setError('Enter a UK mobile as 07xxx xxxxxx.');
      return;
    }

    setSaving(true);
    try {
      // Duplicate check first — if the shop already has this person on
      // file, pop the "This number already belongs to X" modal.
      const { data: dup } = await supabase.rpc('check_customer_duplicate', {
        _shop_id: shopId,
        _email:   form.email || '',
        _phone:   form.phone || '',
      });
      if (dup && dup.id) {
        setDuplicateCandidate(dup);
        setSaving(false);
        return;
      }

      // Route the INSERT through the authenticated SECURITY DEFINER RPC
      // so it works with the tightened RLS (batch-c-customers-insert.sql):
      // direct `.from('customers').insert()` from the client is blocked
      // for staff unless there's an explicit policy match.
      const { data: newId, error: ce } = await supabase.rpc(
        'find_or_create_customer_authed',
        {
          _shop_id: shopId,
          _name:    form.name,
          _email:   form.email || '',
          _phone:   form.phone || '',
        },
      );
      if (ce) throw ce;
      if (!newId) throw new Error('Customer create returned no id');
      onCreated();
    } catch (e) {
      setError(e.message || 'Could not add client');
    } finally {
      setSaving(false);
    }
  };

  const handleUseExisting = () => {
    // In the Clients context "use existing" just closes the modal —
    // the client already exists so there's nothing to add. The user
    // can click through to that client's record instead.
    setDuplicateCandidate(null);
    onClose();
  };
  const handleEditEntry = () => {
    setDuplicateCandidate(null);
  };

  return (
    <div className="modal-backdrop" onClick={onClose}>
      <form className="modal-card" onClick={(e) => e.stopPropagation()} onSubmit={submit} data-testid="add-client-modal">
        <h3>Add client</h3>
        <div className="modal-sub">Quick add — bookings can be linked later.</div>
        {error && <div className="modal-error">{error}</div>}
        <div className="field">
          <label>Name</label>
          <input className="inp" required value={form.name}
            onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="ac-name" />
        </div>
        <div className="field-row">
          <div className="field">
            <PhoneInput
              value={form.phone}
              hideNonUKToggle
              onChange={({ value }) =>
                setForm({ ...form, phone: value })
              }
              required={false}
              testIdPrefix="ac-phone"
            />
          </div>
          <div className="field">
            <label>Email</label>
            <input className="inp" type="email" value={form.email}
              onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="ac-email" />
          </div>
        </div>
        <div className="modal-actions">
          <button type="button" className="btn s" onClick={onClose}>Cancel</button>
          <button type="submit" className="btn p" disabled={saving} data-testid="ac-submit">
            {saving ? 'Saving…' : 'Add client'}
          </button>
        </div>
      </form>
      {duplicateCandidate && (
        <DuplicateCustomerModal
          existing={duplicateCandidate}
          matchedField={duplicateCandidate.matched_on}
          onUseExisting={handleUseExisting}
          onEdit={handleEditEntry}
        />
      )}
    </div>
  );
};

const Clients = () => (
  <AppShell>
    <ClientsInner />
  </AppShell>
);

export default Clients;
